import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "./icons";
import { Input, cx } from "./ui";

/**
 * A search box whose value is lifted only once typing settles.
 *
 * Debounced at 250ms, which is the same figure the operator console uses — a
 * query per keystroke is a request per keystroke when the filter is served by
 * the API, and this one is. The input stays controlled locally so the caret
 * never jumps while a slower response lands.
 */
export function SearchInput({
  value,
  onChange,
  placeholder,
  className,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}) {
  const { t } = useTranslation("common");
  const [draft, setDraft] = useState(value);

  // Adopt an external reset — clearing a filter elsewhere — without clobbering
  // what somebody is in the middle of typing.
  useEffect(() => {
    setDraft((current) => (value === "" && current !== "" ? "" : current));
  }, [value]);

  useEffect(() => {
    if (draft === value) return;
    const timer = setTimeout(() => onChange(draft), 250);
    return () => clearTimeout(timer);
  }, [draft, value, onChange]);

  return (
    <div className={cx("relative", className)}>
      <Icon
        name="search"
        className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-fg-subtle"
      />
      <Input
        type="search"
        value={draft}
        placeholder={placeholder ?? t("search")}
        onChange={(event) => setDraft(event.target.value)}
        className="pl-8"
      />
      {draft && (
        <button
          type="button"
          onClick={() => {
            setDraft("");
            onChange("");
          }}
          aria-label={t("searchInput.clear")}
          className="absolute top-1/2 right-2 grid size-5 -translate-y-1/2 place-items-center text-fg-subtle transition-colors hover:text-fg"
        >
          <Icon name="close" className="size-3" />
        </button>
      )}
    </div>
  );
}
